import React from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

type XeroCustomerAddress = {
    addressType?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    region?: string | null;
    postalCode?: string | null;
    country?: string | null;
    attentionTo?: string | null;
};

type XeroCustomerPhone = {
    phoneType?: string | null;
    phoneNumber?: string | null;
    phoneAreaCode?: string | null;
    phoneCountryCode?: string | null;
};

type XeroCustomerContactDetails = {
    firstName?: string | null;
    lastName?: string | null;
    emailAddress?: string | null;
    addresses?: XeroCustomerAddress[];
    phones?: XeroCustomerPhone[];
};

type Props = {
    loading: boolean;
    detail?: XeroCustomerContactDetails | null;
};

const hasAddressContent = (address: XeroCustomerAddress) => {
    return !!(address.addressLine1 || address.addressLine2 || address.city || address.region || address.postalCode || address.country || address.attentionTo);
};

const hasPhoneContent = (phone: XeroCustomerPhone) => {
    return !!(phone.phoneNumber || phone.phoneAreaCode || phone.phoneCountryCode);
};

const formatPhone = (phone: XeroCustomerPhone) => {
    return [phone.phoneCountryCode, phone.phoneAreaCode, phone.phoneNumber]
        .filter(Boolean)
        .join(' ');
};

export default function XeroCustomerContactDetailsPanel({ loading, detail }: Props) {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (!detail) {
        return null;
    }

    const addresses = (detail.addresses || []).filter(hasAddressContent);
    const phones = (detail.phones || []).filter(hasPhoneContent);

    return (
        <Box
            sx={{
                p: 2,
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(3, minmax(0, 1fr))'
                },
                alignItems: 'start'
            }}
        >
            <Box>
                <Typography variant="subtitle2">Contact</Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="body2">
                        {[detail.firstName, detail.lastName].filter(Boolean).join(' ') || '-'}
                    </Typography>
                    <Typography variant="body2">{detail.emailAddress || '-'}</Typography>
                </Stack>
            </Box>

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Phone Numbers</Typography>
                {phones.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No phone numbers available.</Typography>
                ) : (
                    <Stack spacing={1}>
                        {phones.map((phone, index) => (
                            <Box key={`${phone.phoneType || 'phone'}-${index}`}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{phone.phoneType || 'Phone'}</Typography>
                                <Typography variant="body2">{formatPhone(phone)}</Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Addresses</Typography>
                {addresses.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No addresses available.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {addresses.map((address, index) => (
                            <Box key={`${address.addressType || 'address'}-${index}`}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{address.addressType || 'Address'}</Typography>
                                {[address.attentionTo, address.addressLine1, address.addressLine2, address.city, address.region, address.postalCode, address.country]
                                    .filter(Boolean)
                                    .map((line, lineIndex) => (
                                        <Typography variant="body2" key={lineIndex}>{line}</Typography>
                                    ))}
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}
